/**
 * editOpportunityForm.js - pure form logic for the record editor (an
 * opportunity and a project are one record, so one editor): the quote math
 * (margin, go x get, anticipated value), building the initial form state from
 * a record, which fields each stage shows, and turning the form back into a
 * save payload. Kept out of the component so the formulas are readable and
 * testable.
 * r50 adds what a save sends (diffForSave), the conflict wording, and the
 * next quote number (fetchNextQuote, the one function here that fetches).
 */
import { nextQuoteNumber } from '../../utils/docSuggest';
import { leftoverOf } from '../../utils/projectMoney';
import { isProjectRow } from '../../data/projectStatus';

export function deriveQuoteMath(f) {
  const price = parseFloat(f.quotedPrice) || 0;
  const labor = parseFloat(f.quotedLaborCost) || 0;
  const expenses = parseFloat(f.quotedExpenses) || 0;
  const totalCogs = labor + expenses;
  const quotedProfit = price - totalCogs;
  // null until a cost exists - a budgetary quote with zero COGS is "not costed", not 100% margin (Paul #47)
  const grossMarginPct = price > 0 && totalCogs > 0 ? (quotedProfit / price) * 100 : null;
  const goPct = parseFloat(f.goPct) || 0;
  const getPct = parseFloat(f.getPct) || 0;
  const goGetPct = (goPct / 100) * (getPct / 100) * 100;
  const anticipatedValue = price * (goPct / 100) * (getPct / 100);
  const marginColor = grossMarginPct == null ? '#aaa' : grossMarginPct >= 30 ? '#1B5E20' : grossMarginPct >= 20 ? '#E65100' : '#B71C1C';
  return { price, labor, expenses, totalCogs, quotedProfit, grossMarginPct, goPct, getPct, goGetPct, anticipatedValue, marginColor };
}

/* Initial field state from a (camelCase or snake_case) record.
   All fields stay nullable — blank entries render as "-".
   `defaultCurrency` fills a blank currency: the GLOBAL KSA tab passes 'SAR'
   (its POs are in SAR); everywhere else it stays 'US Dollar'.
   Topic and Name are one field, "Name" (10/8/26): a record that only ever
   had a topic shows it as its name (the server keeps topic = name). */
const asText = (v) => (v == null ? '' : String(v));
export function buildInitialForm(opp, isCreate, user, { defaultCurrency = 'US Dollar' } = {}) {
  const pick = (a, b) => opp[a] ?? opp[b];
  return {
    name: opp.name || opp.topic || '',
    customer: opp.customer || '',
    subCustomer: opp.subCustomer || opp.sub_customer || '',
    customerProjectNumber: opp.customerProjectNumber || opp.customer_project_number || '',
    description: opp.description || '',
    approvalManager: opp.approvalManager || opp.approval_manager || '',
    pm: opp.pm || '',
    globalProjectNumber: opp.globalProjectNumber || opp.global_project_number || '',
    engineer: opp.engineer || '',
    division: opp.division || '',
    type: opp.type || '',
    quoteLetter: opp.quoteLetter || opp.quote_letter || '',
    rev: opp.rev || '',
    poDateIssued: opp.poDateIssued || opp.po_date_issued || '',
    poNumber: opp.poNumber || opp.po_number || '',
    // the project's money and state: the PO value, the payment terms, what
    // has been invoiced, and Active / Closed (projects.closed: 'YES' or blank)
    poValue: asText(pick('poValue', 'po_value')),
    terms: opp.terms || '',
    invoicedAlready: asText(pick('invoicedAlready', 'invoiced_already')),
    closed:
      String(opp.closed || '')
        .trim()
        .toUpperCase() === 'YES'
        ? 'YES'
        : '',
    dueDate: opp.dueDate || opp.due_date || '',
    // Default new opportunities to today — a missing quote date makes the row
    // invisible in every list (the validity filter requires one).
    quoteDate: opp.quoteDate || opp.quote_date || (isCreate ? new Date().toISOString().slice(0, 10) : ''),
    estCloseDate: opp.estCloseDate || opp.est_close_date || '',
    latestInvoiceDate: opp.latestInvoiceDate || opp.latest_invoice_date || '',
    invoiceNumbers: opp.invoiceNumbers || opp.invoice_numbers || '',
    // New opps default to the signed-in user so a BD owns what they create.
    owner: opp.owner || (isCreate && user ? user.email : ''),
    status: opp.status || 'Open',
    salesStage: opp.salesStage || opp.sales_stage || 'Qualify',
    // the opportunity's "Quote sent" (projects.quote_sent, a yes / no)
    quoteSent: pick('quoteSent', 'quote_sent') ? 'Yes' : 'No',
    currency: opp.currency || defaultCurrency,
    billingType: opp.billingType || opp.billing_type || '',
    quotedPrice: opp.quotedPrice != null && opp.quotedPrice !== '' ? String(opp.quotedPrice) : '',
    quotedLaborCost: opp.quotedLaborCost != null && opp.quotedLaborCost !== '' ? String(opp.quotedLaborCost) : '',
    quotedExpenses: opp.quotedExpenses != null && opp.quotedExpenses !== '' ? String(opp.quotedExpenses) : '',
    goPct: opp.goPct != null ? String(opp.goPct) : '',
    getPct: opp.getPct != null ? String(opp.getPct) : '',
    // App-owned forecast fields
    bookDate: opp.bookDate || opp.book_date || '',
    billDate: opp.billDate || opp.bill_date || '',
    probability: opp.probability != null ? String(opp.probability) : '',
    quoteType: opp.quoteType || opp.quote_type || '',
    // A-02: the exact CRM site (or site-less account) this opportunity is
    // for. A seed { customer, status, accountId } arrives pre-linked.
    accountId: opp.accountId || opp.account_id || '',
    // the record this one sits under (10/14/26, projects.parent_id): '' follows the Global Project Number
    parentId: opp.parentId || opp.parent_id || '',
    // why it was Lost / Cancelled / No Quote (11/3/26)
    closeReason: opp.closeReason || opp.close_reason || '',
  };
}

/* Save payload. `linkSite` false leaves account_id out entirely (the GLOBAL
   KSA tab has no CRM accounts to link to). '' clears the link.
   Never sent: topic (the server keeps it equal to the name), phase and
   progress % (meaningless - every row was Proposal / 0), and the old quote
   list's project #, tracking sheet and internal name (shown read-only).
   The leftover is the PO value minus what is invoiced (utils/projectMoney),
   never typed: sent when it changes, never guarded (OPP_UNGUARDED).
   `ledger` (the main tab, 10/11/26): invoicing is the invoice ledger, and the
   server works Invoiced, Invoice #'s, Latest Invoice Date and the Leftover
   out of it - so none of the four is ever sent (LEDGER_FIELDS). The GLOBAL
   KSA tab keeps typing them. */
export const LEDGER_FIELDS = ['invoicedAlready', 'moneyLeftover', 'latestInvoiceDate', 'invoiceNumbers'];
const numOrNull = (v) => (String(v ?? '').trim() === '' ? null : parseFloat(v));
const trimOrNull = (v) => String(v || '').trim() || null;
export function buildSavePayload(f, derived, { linkSite = true, ledger = false } = {}) {
  const out = buildFullPayload(f, derived, linkSite);
  if (ledger) for (const k of LEDGER_FIELDS) delete out[k];
  return out;
}
function buildFullPayload(f, derived, linkSite) {
  const { price, labor, expenses, quotedProfit, grossMarginPct, goPct, getPct, anticipatedValue } = derived;
  return {
    name: f.name,
    customer: f.customer,
    subCustomer: f.subCustomer,
    customerProjectNumber: f.customerProjectNumber,
    description: f.description,
    approvalManager: f.approvalManager,
    pm: f.pm,
    // always shown, never required (r49): it is often issued after the PO
    globalProjectNumber: trimOrNull(f.globalProjectNumber),
    engineer: f.engineer,
    division: f.division,
    type: f.type,
    quoteLetter: f.quoteLetter,
    rev: trimOrNull(f.rev),
    poDateIssued: f.poDateIssued,
    poNumber: f.poNumber,
    dueDate: f.dueDate,
    poValue: numOrNull(f.poValue),
    terms: trimOrNull(f.terms),
    invoicedAlready: numOrNull(f.invoicedAlready),
    moneyLeftover: leftoverOf({ poValue: f.poValue, invoicedAlready: f.invoicedAlready }),
    closed: f.closed === 'YES' ? 'YES' : null,
    quoteDate: f.quoteDate,
    estCloseDate: f.estCloseDate,
    owner: f.owner,
    latestInvoiceDate: f.latestInvoiceDate,
    invoiceNumbers: f.invoiceNumbers,
    status: f.status,
    salesStage: f.salesStage,
    quoteSent: f.quoteSent === 'Yes',
    currency: f.currency,
    billingType: f.billingType,
    quotedPrice: price,
    quotedLaborCost: labor,
    quotedExpenses: expenses,
    quotedProfit,
    quotedGrossMargin: grossMarginPct,
    goPct,
    getPct,
    anticipatedValue,
    bookDate: f.bookDate || null,
    billDate: f.billDate || null,
    probability: f.probability === '' ? null : Number(f.probability),
    quoteType: f.quoteType || null,
    closeReason: trimOrNull(f.closeReason),
    // the main tab's links: the CRM site, and the Main project (GLOBAL KSA has neither)
    ...(linkSite ? { accountId: f.accountId || null, parentId: f.parentId || null } : {}),
  };
}

/* ── Which fields each stage shows (Sharif, 10/8/26) ──
   One record: Open / Lost / Cancelled / No Quote is an opportunity, Won /
   Complete a project (data/projectStatus). Some fields only mean something
   at one stage, and the editor hides them at the other; it follows the
   Status in the form, so picking Won shows the PO fields at once. A value in
   a hidden field is kept - hiding never clears it, and a save only sends
   what was changed (diffForSave). Every other field shows at both stages. */
export const OPPORTUNITY_ONLY = [
  'salesStage',
  'probability',
  'goPct',
  'getPct',
  'estCloseDate',
  'dueDate',
  'quoteSent',
  'anticipatedValue',
  'bookDate',
];
export const PROJECT_ONLY = [
  'poNumber',
  'poDateIssued',
  'poValue',
  'terms',
  'pm',
  'latestInvoiceDate',
  'invoiceNumbers',
  'invoicedAlready',
  'moneyLeftover',
  'closed',
  'billDate',
];
/** True when the form's Status makes the record a project (Won / Complete). */
export const isProjectForm = (f) => isProjectRow({ status: f && f.status });
/** Whether field `key` shows at the stage form `f` is at. */
export function showsField(key, f) {
  if (OPPORTUNITY_ONLY.includes(key)) return !isProjectForm(f);
  if (PROJECT_ONLY.includes(key)) return isProjectForm(f);
  return true;
}

/* The old quote list's three columns: filled only by its last import (the
   list was retired 10/1/26) and edited nowhere. Shown read-only, on a project
   that has any of them; the editor never sends them. */
export const OLD_QUOTE_LIST_FIELDS = [
  ['projectNumber', 'project_number', 'Old project #'],
  ['tracking', 'tracking', 'Tracking sheet'],
  ['internalName', 'internal_name', 'Internal name'],
];
/** [{ label, value }] of the old quote list's fields this record has; [] when none. */
export function oldQuoteListValues(opp) {
  const o = opp || {};
  return OLD_QUOTE_LIST_FIELDS.map(([camel, snake, label]) => ({ label, value: String(o[camel] ?? o[snake] ?? '').trim() })).filter(
    (x) => x.value
  );
}

/* Fields saved somewhere else while the editor is open, merged into its form
   so the editor's next Save sends them instead of the values it opened with.
   The case (Q-02): the Tasks tab's next-step prompt closes the record as Won
   and its Won project setup saves the PO number, PO value, PO date, billing
   type and division; without this, a later Save sent the editor's blank PO
   fields and wiped them. Only keys the form has are taken, and a null /
   undefined value is ignored. */
export function mergeSavedFields(f, patch) {
  if (!patch || typeof patch !== 'object') return f;
  let out = f;
  for (const [k, v] of Object.entries(patch)) {
    if (v == null || !Object.prototype.hasOwnProperty.call(f, k)) continue;
    if (out === f) out = { ...f };
    out[k] = String(v);
  }
  return out;
}

/* ── r50: a save sends only what changed, and never over someone else's save ──
   The editors used to send the whole form on every save, so saving quietly
   wrote back the old value of every field someone else had changed since the
   editor opened. Now a save of an EXISTING record sends only the fields the
   user edited - their value is no longer the one in the snapshot taken when
   the editor opened - with `expect` = the value each of those had as loaded;
   the server refuses the save (409 edit_conflict) when one of THOSE has
   changed underneath. A new record still sends everything. The record
   editor (EditOpportunityModal) saves opportunities and projects this way. */

const BLANK = (v) => v === null || v === undefined || v === '';
const ISO_STAMP = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/;
const dayOf = (v) => {
  const m = String(v).match(ISO_STAMP);
  return m ? m[1] : String(v);
};
const snakeOf = (k) => String(k).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
/** 'quoted_price' -> 'quotedPrice' (the server names columns, the forms use camelCase). */
export const camelOf = (k) => String(k).replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

/** Same value for a save: blank equals blank (null, '' and undefined), numbers
 *  compare as numbers ('60000.00' and 60000), a timestamp on its day. */
export function sameValue(a, b) {
  if (BLANK(a) || BLANK(b)) return BLANK(a) && BLANK(b);
  if (typeof a === 'number' || typeof b === 'number') {
    const x = Number(a),
      y = Number(b);
    if (Number.isFinite(x) && Number.isFinite(y)) return x === y;
  }
  return dayOf(a) === dayOf(b);
}

/* Fields worked out from others (deriveQuoteMath), each with the fields it
   comes from. An edit never sends them: when a save carries one of those
   fields, the server works the figure out again from its row as it is now
   plus what is sent (routes/projects.js, routes/ksa.js). Worked out here, a
   margin could use a labor cost someone else has since changed - and sending
   that labor cost along to make it right would write their change back.
   A new record sends them with everything else. */
export const OPP_DERIVED = {
  quotedProfit: ['quotedPrice', 'quotedLaborCost', 'quotedExpenses'],
  quotedGrossMargin: ['quotedPrice', 'quotedLaborCost', 'quotedExpenses'],
  anticipatedValue: ['quotedPrice', 'goPct', 'getPct'],
};
/* The leftover (PO value - invoiced) is stored as sent: an edit sends it when
   it changes, but never guards it - nobody types it, so a guard could only
   trip over someone else's change to the PO value or the invoiced amount. */
export const OPP_UNGUARDED = ['moneyLeftover'];

/** A record's own value for a payload key, camelCase or snake_case; undefined when it has neither. */
export function rawValue(row, key) {
  if (!row || typeof row !== 'object') return undefined;
  return row[key] !== undefined ? row[key] : row[snakeOf(key)];
}

/** The record's values as loaded, for these payload keys (a key it lacks is left out). */
export function loadedValues(row, keys) {
  const out = {};
  for (const k of keys || []) {
    const v = rawValue(row, k);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * What a save of an existing record sends: `changes`, the payload fields the
 * user edited (their value is not the one the snapshot - the payload as the
 * editor opened - holds), and nothing else; and `expect`, the value each of
 * those had as loaded (`loaded`, the record's own values - not the form's:
 * the form shows a blank name as the old topic, a blank stage as Qualify, and
 * expecting those would refuse every save). A field the record was loaded
 * without is sent unguarded rather than guarded by a guess. So a conflict can
 * only ever name a field edited here, and "Keep mine" only forces those.
 *   derived    worked-out fields the server works out itself (OPP_DERIVED):
 *              never sent
 *   unguarded  worked-out fields the server stores as sent: sent when they
 *              change, never guarded - nobody types them, so a guard could
 *              only trip over someone else's change to a field they come from
 */
export function diffForSave(payload, snapshot, loaded = {}, { derived = {}, unguarded = [] } = {}) {
  const snap = snapshot || {};
  const skip = derived || {};
  const changes = {};
  for (const [k, v] of Object.entries(payload || {})) {
    if (k in skip) continue;
    if (!sameValue(v, snap[k])) changes[k] = v;
  }
  const expect = {};
  for (const k of Object.keys(changes)) {
    if ((unguarded || []).includes(k) || !loaded || loaded[k] === undefined) continue;
    expect[k] = loaded[k];
  }
  return { changes, expect };
}

/* "Use theirs" after an edit conflict: their values into the form, for the
   fields the form has; blank as '', a timestamp as its day, the rest as text
   (the form's inputs hold strings). Every other field keeps what was typed. */
export function applyTheirs(f, values) {
  if (!values || typeof values !== 'object') return f;
  let out = f;
  for (const [k, v] of Object.entries(values)) {
    if (!Object.prototype.hasOwnProperty.call(f, k)) continue;
    if (out === f) out = { ...f };
    out[k] = BLANK(v) ? '' : dayOf(v);
  }
  return out;
}

/** The conflicts of a 409 edit_conflict body, keyed the way the forms are. */
export function conflictsOf(body) {
  return (body && Array.isArray(body.conflicts) ? body.conflicts : [])
    .filter((c) => c && c.field)
    .map((c) => ({ ...c, key: camelOf(c.field) }));
}

/** Their values of a 409 edit_conflict, as { formKey: value }. */
export const theirValues = (body) => Object.fromEntries(conflictsOf(body).map((c) => [c.key, c.theirs]));

/** A field value as the conflict dialog shows it. */
export const showValue = (v) => (BLANK(v) ? '(blank)' : dayOf(v));

/** "3:42 PM" today, "Oct 1, 3:42 PM" another day; '' when there is no time. */
export function whenSaved(at, now = new Date()) {
  const d = at ? new Date(at) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** One line of the conflict dialog: "<label>: <actor> saved <theirs> at <time>; yours: <yours>". */
export function conflictLine(c, changedBy, labels = {}) {
  const label = fieldLabel(c.key || camelOf(c.field), labels);
  const actor = (changedBy && changedBy.actor) || 'someone else';
  const at = changedBy ? whenSaved(changedBy.at) : '';
  return `${label}: ${actor} saved ${showValue(c.theirs)}${at ? ` at ${at}` : ''}; yours: ${showValue(c.yours)}`;
}

/* The record editor's own labels (EditOpportunityTabs / Header), for the
   conflict dialog and the "someone just changed this record" banner. A field
   the editor does not have (tracking, health...) has none, so someone else's
   change to it raises no banner. */
export const OPP_FIELD_LABELS = {
  name: 'Name',
  customer: 'Customer',
  subCustomer: 'Sub Customer',
  customerProjectNumber: 'Customer Project / RFQ #',
  description: 'Description',
  approvalManager: 'Approval Manager',
  pm: 'Project Manager',
  globalProjectNumber: 'Global Project Number',
  engineer: 'Engineer(s)',
  division: 'Division',
  type: 'Type',
  quoteLetter: 'Quote #',
  rev: 'Revision #',
  poDateIssued: 'PO Date Issued',
  poNumber: 'PO Number',
  poValue: 'PO Value',
  terms: 'Net Terms',
  dueDate: 'Due Date',
  quoteDate: 'Quote Date',
  estCloseDate: 'Est. Close Date',
  owner: 'Owner',
  latestInvoiceDate: 'Latest Invoice Date',
  invoiceNumbers: "Invoice #'s",
  invoicedAlready: 'Invoiced (amount)',
  moneyLeftover: 'Leftover',
  closed: 'Active / Closed',
  quoteSent: 'Quote Sent',
  status: 'Status',
  salesStage: 'Sales Stage',
  currency: 'Currency',
  billingType: 'Billing Type',
  quotedPrice: 'Quoted Price',
  quotedLaborCost: 'Quoted Labor Cost',
  quotedExpenses: 'Quoted Expenses',
  quotedProfit: 'Quoted Profit',
  quotedGrossMargin: 'Gross Margin',
  goPct: 'Go %',
  getPct: 'Get %',
  anticipatedValue: 'Anticipated Value',
  bookDate: 'Book Date',
  billDate: 'Bill Date',
  probability: '% Prob',
  quoteType: 'Quote Type',
  accountId: 'Site',
  parentId: 'Main project',
  closeReason: 'Why',
};

/** A field's label: the form's own, else its name in words ('po_value' -> 'Po value'). */
export function fieldLabel(key, labels = {}) {
  const k = camelOf(key);
  if (labels[k]) return labels[k];
  const words = k
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* r50: a new record's quote number. The browser used to guess it from the
   rows it had loaded (nextQuoteNumber), so two people opening "New" together
   got the same number; the server knows every record, main and GLOBAL KSA
   (one series), so it is asked first and the guess is only the fallback. */
export async function fetchNextQuote(base, allProjects) {
  try {
    const r = await fetch(`${base}/projects/next-quote`);
    const b = r && r.ok ? await r.json() : null;
    const n = b && b.next != null ? String(b.next).trim() : '';
    if (n) return n;
  } catch {
    /* offline / older server: the guess below */
  }
  return nextQuoteNumber(allProjects) || '';
}

/** The number a new record's Quote field takes when `next` is offered, or null
 *  to leave it: a blank field or our own earlier offer takes it; a number the
 *  user typed is never overwritten. */
export function takeOfferedQuote(current, lastOffered, next) {
  const cur = String(current == null ? '' : current).trim();
  const n = String(next == null ? '' : next).trim();
  if (!n || cur === n) return null;
  if (cur && cur !== String(lastOffered == null ? '' : lastOffered).trim()) return null;
  return n;
}

/* r49 searchable Customer: what a typed customer name settles to when the
   field is left. Spaces are tidied the way the server tidies them; an exact
   case-insensitive match takes the list's (canonical) spelling. Returns
   { kind: 'blank' } | { kind: 'match', name } | { kind: 'unknown', name } -
   'unknown' is a name the list does not have, which the field offers to add. */
export function settleCustomer(typed, list = []) {
  const name = String(typed == null ? '' : typed)
    .trim()
    .replace(/\s+/g, ' ');
  if (!name) return { kind: 'blank' };
  const lower = name.toLowerCase();
  const hit = (list || []).find((n) => String(n).trim().toLowerCase() === lower);
  return hit ? { kind: 'match', name: hit } : { kind: 'unknown', name };
}

/* Field-row renderer factory. IMPORTANT: renderRow stays a plain render
   FUNCTION called as {renderRow(...)} — never a component type — so React
   reconciles its inputs in place and typing never loses focus (see the
   Section comment above). Field state (f/setF) lives in the modal and is
   passed in explicitly. */
